import { useState, useMemo } from 'react'
import { Button, Chip, Select, Input, Avatar, Alert, EmptyState } from '@/components/ui'
import { Download, Bot, Loader2, RefreshCw } from 'lucide-react'
import { fmtMoney, cn } from '@/lib/utils'
import { useHistoricoComprobantes, type ComprobanteRow, type EstadoComprobante } from '@/lib/api/comprobantes'

type EstadoFiltro = 'todos' | 'validado' | 'rechazado' | 'pedir_correcto'
type AgenteFiltro = 'todos' | 'ia' | 'humano'

const estadoChip: Record<EstadoComprobante, { label: string; color: 'emerald' | 'rose' | 'amber' | 'default' }> = {
  pendiente: { label: '⏳ Pendiente', color: 'default' },
  en_revision_humana: { label: '👁 En revisión', color: 'amber' },
  pedir_correcto: { label: '⏸ Pedir correcto', color: 'amber' },
  validado_auto: { label: '✓ Validado IA', color: 'emerald' },
  validado_manual: { label: '✓ Validado agente', color: 'emerald' },
  rechazado: { label: '✗ Rechazado', color: 'rose' },
}

export function Historico() {
  const { data: historico = [], isLoading, error, refetch, isRefetching } = useHistoricoComprobantes(500)

  // Default: últimos 30 días
  const hoy = new Date()
  const hace30d = new Date(hoy.getTime() - 30 * 86_400_000)
  const [desde, setDesde] = useState<string>(hace30d.toISOString().slice(0, 10))
  const [hasta, setHasta] = useState<string>(hoy.toISOString().slice(0, 10))
  const [estado, setEstado] = useState<EstadoFiltro>('todos')
  const [banco, setBanco] = useState<string>('todos')
  const [agente, setAgente] = useState<AgenteFiltro>('todos')

  // Solo resueltos (quitamos pendiente / en_revision_humana que viven en el otro tab)
  const resueltos = useMemo(
    () =>
      historico.filter(c =>
        ['validado_auto', 'validado_manual', 'rechazado', 'pedir_correcto'].includes(c.estado)
      ),
    [historico]
  )

  const bancosDisponibles = useMemo(() => {
    const set = new Set<string>()
    for (const c of resueltos) {
      if (c.banco_extraido) set.add(c.banco_extraido)
    }
    return Array.from(set).sort()
  }, [resueltos])

  const filtrado = useMemo(() => {
    const desdeMs = desde ? new Date(desde + 'T00:00:00').getTime() : 0
    const hastaMs = hasta ? new Date(hasta + 'T23:59:59').getTime() : Infinity

    return resueltos.filter(c => {
      const creadoMs = new Date(c.creado_en).getTime()
      if (creadoMs < desdeMs || creadoMs > hastaMs) return false

      if (estado !== 'todos') {
        if (estado === 'validado' && !['validado_auto', 'validado_manual'].includes(c.estado)) return false
        if (estado === 'rechazado' && c.estado !== 'rechazado') return false
        if (estado === 'pedir_correcto' && c.estado !== 'pedir_correcto') return false
      }

      if (banco !== 'todos') {
        if (!c.banco_extraido || !c.banco_extraido.toLowerCase().includes(banco.toLowerCase())) return false
      }

      if (agente !== 'todos') {
        const esIA = c.estado === 'validado_auto'
        if (agente === 'ia' && !esIA) return false
        if (agente === 'humano' && esIA) return false
      }

      return true
    })
  }, [resueltos, desde, hasta, estado, banco, agente])

  const exportCsv = () => {
    const header = [
      'id', 'cliente', 'cedula', 'whatsapp', 'monto_extraido',
      'fecha_extraida', 'banco_extraido', 'ultimos_4', 'titular',
      'referencia', 'estado', 'confianza_ia', 'resuelto_at', 'nota',
    ].join(',')
    const rows = filtrado.map(c =>
      [
        c.id,
        esc(c.cliente_nombre),
        c.cedula,
        c.numero_whatsapp,
        c.monto_extraido ?? '',
        c.fecha_extraida ?? '',
        esc(c.banco_extraido),
        c.ultimos_4_extraidos ?? '',
        esc(c.titular_extraido),
        c.numero_referencia ?? '',
        c.estado,
        c.confianza_ia !== null ? Math.round(c.confianza_ia * 100) : '',
        c.revisado_at ?? '',
        esc(c.revision_nota),
      ].join(',')
    )
    const blob = new Blob([[header, ...rows].join('\n')], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `historico_comprobantes_${desde}_${hasta}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
        <span className="ml-2 text-slate-500">Cargando histórico…</span>
      </div>
    )
  }

  if (error) {
    return (
      <Alert variant="danger" title="Error al cargar histórico">
        {(error as Error).message}
      </Alert>
    )
  }

  return (
    <div className="space-y-5">
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-end gap-3">
        <Input label="Desde" type="date" value={desde} onChange={e => setDesde(e.target.value)} className="w-40" />
        <Input label="Hasta" type="date" value={hasta} onChange={e => setHasta(e.target.value)} className="w-40" />
        <div className="w-px h-10 bg-slate-200" />
        <Select label="Agente" value={agente} onChange={e => setAgente(e.target.value as AgenteFiltro)} className="w-44">
          <option value="todos">Todos los agentes</option>
          <option value="ia">🤖 IA Auto</option>
          <option value="humano">👤 Humano</option>
        </Select>
        <Select label="Estado" value={estado} onChange={e => setEstado(e.target.value as EstadoFiltro)} className="w-44">
          <option value="todos">Todos los estados</option>
          <option value="validado">✓ Validados</option>
          <option value="rechazado">✗ Rechazados</option>
          <option value="pedir_correcto">⏸ Pedir correcto</option>
        </Select>
        <Select label="Banco" value={banco} onChange={e => setBanco(e.target.value)} className="w-40">
          <option value="todos">Todos los bancos</option>
          {bancosDisponibles.map(b => (
            <option key={b} value={b}>{b}</option>
          ))}
        </Select>
        <div className="ml-auto flex items-center gap-3">
          <span className="text-xs text-slate-500">
            <span className="font-semibold text-navy-700">{filtrado.length}</span> de {resueltos.length} resueltos
          </span>
          <Button variant="ghost" size="sm" onClick={() => refetch()} disabled={isRefetching}>
            <RefreshCw className={cn('w-3.5 h-3.5', isRefetching && 'animate-spin')} />
          </Button>
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={filtrado.length === 0}>
            <Download className="w-3.5 h-3.5" /> Export CSV
          </Button>
        </div>
      </div>

      {filtrado.length === 0 ? (
        <EmptyState
          title="Sin resultados"
          description="Ajustá los filtros o ampliá el rango de fechas."
        />
      ) : (
        <section className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-600 font-semibold">
                <tr>
                  <th className="text-left px-4 py-2">Cliente</th>
                  <th className="text-right px-4 py-2">Monto</th>
                  <th className="text-left px-4 py-2">Banco</th>
                  <th className="text-left px-4 py-2">Fecha comp.</th>
                  <th className="text-left px-4 py-2">Resuelto por</th>
                  <th className="text-right px-4 py-2">Confianza IA</th>
                  <th className="text-left px-4 py-2">Estado</th>
                  <th className="text-left px-4 py-2">Resuelto</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtrado.map(c => {
                  const est = estadoChip[c.estado]
                  const esIA = c.estado === 'validado_auto'
                  const ok = c.estado === 'validado_auto' || c.estado === 'validado_manual'
                  const cuando = relativo(c.creado_en)
                  const resueltoCuando = c.revisado_at ? relativo(c.revisado_at) : '—'
                  return (
                    <tr key={c.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Avatar name={c.cliente_nombre ?? c.cedula} size="sm" />
                          <div>
                            <div className="font-medium text-navy-700">
                              {c.cliente_nombre ?? <span className="text-slate-400">sin vincular</span>}
                            </div>
                            <div className="text-[11px] text-slate-500 font-mono">
                              {c.cedula} · {cuando}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className={cn('px-4 py-3 text-right font-mono font-semibold', ok ? 'text-emerald-600' : 'text-slate-400')}>
                        {c.monto_extraido !== null ? fmtMoney(c.monto_extraido) : '—'}
                      </td>
                      <td className="px-4 py-3 text-slate-700">{c.banco_extraido ?? '—'}</td>
                      <td className="px-4 py-3 font-mono text-slate-700">
                        {c.fecha_extraida ?? '—'}
                      </td>
                      <td className="px-4 py-3">
                        {esIA ? (
                          <Chip variant="blue"><Bot className="w-3 h-3 mr-1 inline" /> IA Auto</Chip>
                        ) : c.revisado_por ? (
                          <span className="text-slate-700 text-xs font-mono">{c.revisado_por.slice(0, 8)}…</span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {c.confianza_ia !== null ? (
                          <span className={cn(
                            'font-mono',
                            c.confianza_ia >= 0.85 ? 'text-emerald-600' : c.confianza_ia >= 0.6 ? 'text-amber-600' : 'text-rose-600'
                          )}>
                            {Math.round(c.confianza_ia * 100)}%
                          </span>
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-4 py-3"><Chip variant={est.color}>{est.label}</Chip></td>
                      <td className="px-4 py-3 text-xs text-slate-500">{resueltoCuando}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  )
}

function esc(v: string | null | undefined): string {
  if (v === null || v === undefined) return ''
  const s = String(v).replace(/"/g, '""')
  return /[",\n]/.test(s) ? `"${s}"` : s
}

function relativo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const min = Math.floor(diff / 60_000)
  if (min < 1) return 'ahora'
  if (min < 60) return `hace ${min} min`
  const h = Math.floor(min / 60)
  if (h < 24) return `hace ${h}h ${min % 60}min`
  const d = Math.floor(h / 24)
  if (d < 7) return `hace ${d}d`
  return new Date(iso).toLocaleDateString('es-EC')
}
